import { ANALITYCS_PAGES } from '@/shared/constants/analytics.constants'
import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
import { InboundFlowRoutes, OutboundFlowRoutes } from '@/shared/types/router.types'
import { formatDate } from '@/shared/utils'
import { TravelDirection } from '@/shared/types/common.types'
import { useUserStore } from '@/shared/store'
import { isProxy, toRaw } from 'vue'
import { type Plan } from '@/shared/types/api.types'

export type AnalyticsCategory = Exclude<TravelDirection, TravelDirection.UNKNOWN>
export type AnalyticsPlanCategory = 'tpl' | 'od'
export type AnalyticsPlanType = 'single' | 'multiple'
export type NewSPAddonName = 'extra_benefits' | 'one_year_policy'
export type SPSection = 'inclusive_cover' | 'extra_benefit_cover'

export interface PurchaseEventParams {
  category: TravelDirection
  coupon?: string | null
  transaction_id: number
  policy_id: string
  num_of_travelers: number
  addons_price?: number
  total_price: number
  list_of_addons?: string[]
  number_of_addons?: number
  choosen_card: string | null
  countries: string[]
}

export interface SwitchAddonsParams {
  addon_name: string
  quote_id: string
  addon_price: number
  state: boolean
  person_name?: string
  index?: string
  num_of_travelers?: string
  category?: TravelDirection
}

export interface ShowPlansEventParams {
  item_name: string
  price: number
  schemecode: string
}

const CLICK_POLICY_WORDINGS_EVENT = 'click_policy_wordings'
const CHANGE_DATES_EVENT = 'change_dates'
const CHANGE_TRAVELERS_EVENT = 'change_travelers'
const CLICK_TO_PROCEED_EVENT = 'click_proceed'
const SHOW_PLANS_EVENT = 'view_item_list'
const SELECT_PLAN_EVENT = 'select_item'
const SELECT_PLAN_PAGE = 'select_plan'
const EXPAND_ON_EVENT = 'expand_on'
const LEARN_MORE_EVENT = 'learn_more'
const VIEW_POPUP_EVENT = 'view_popup'

export const formatAnalyticsDate = (date: Date, mask = 'dd.MM.yyyy') => formatDate(date, 'en', mask)

const getInsuredTravellersPage = (): string => {
  const userStore = useUserStore()
  if (userStore.activeInsuredTravelersSubPage === 'edit') {
    return 'update_information'
  }

  if (userStore.activeInsuredTravelersSubPage === 'add') {
    return 'add_person'
  }

  return 'insured_people'
}

export class GoogleAnalyticsService {
  readonly productLabel: string

  constructor(product: string) {
    this.productLabel = product
  }

  private send(event: string, payload: Record<string, unknown> = {}) {
    if (typeof window.gtag !== 'function') {
      throw new TypeError('[QIC-Analytics] GoogleAnalyticsService called but gtag is not defined')
    }

    const patchedPayload = {
      product: this.productLabel,
      ...payload,
    }

    const args: ['event', string, any?] = ['event', event]
    if (patchedPayload) {
      args.push(patchedPayload)
    }
    window.gtag(...args)

    console.info(`[QIC-Analytics] Event ${event} is sent by GoogleAnalyticsService`)
    console.info(`%c [QIC-Analytics] Event payload: `, 'background: #5927FF; color: #fff')
    console.info(JSON.stringify(patchedPayload, null, '\t'))
  }

  clickToCheckPrice(category: AnalyticsCategory, country: string[]) {
    this.send('click_to_check_price', {
      position: 'top',
      category,
      country,
    })
  }

  clickPolicyWordings<T>(category?: T, paramName: string = 'category') {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      [paramName]: category,
    })
  }

  clickToCheckPriceLanding(category: AnalyticsCategory) {
    this.send('click_to_check_price', {
      position: 'bottom',
      category,
    })
  }

  clickWhatIsCovered(category: AnalyticsCategory) {
    this.send('what_is_covered', {
      position: 'bottom',
      category,
    })
  }

  sendToMainPage(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    let page = pageData.page
    const category = pageData.category

    if (page === 'insured_people') {
      page = getInsuredTravellersPage()
    }

    this.send('to_main_page', {
      category,
      page,
    })
  }

  sendClickPrivacyPolicy(routeName: InboundFlowRoutes | OutboundFlowRoutes, place?: string) {
    const pageData = ANALITYCS_PAGES[routeName]
    if (!pageData) return

    const category = pageData.category
    let page = pageData.page

    if (page === 'insured_people') {
      page = getInsuredTravellersPage()
    }

    this.send('click_privacy_policy', {
      category,
      page,
      place,
    })
  }

  sendSelectCountryProceed(country: string) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.INBOUND,
      page: 'select_country',
      country: [country],
    })
  }

  sendTravelDatesProceed(
    category: AnalyticsCategory,
    tripType: 'single' | 'multi',
    departureDate: Date,
    returnDate: Date,
    countries: string[]
  ) {
    if (category === TravelDirection.OUTBOUND) {
      return this.send(CLICK_TO_PROCEED_EVENT, {
        category,
        page: ANALITYCS_PAGES[OutboundFlowRoutes.DATES]?.page,
        trip_type: tripType,
        departure: formatAnalyticsDate(departureDate),
        return: formatAnalyticsDate(returnDate),
        countries,
      })
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: ANALITYCS_PAGES[OutboundFlowRoutes.DATES]?.page,
      departure: formatAnalyticsDate(departureDate),
      return: formatAnalyticsDate(returnDate),
    })
  }

  sendNumberOfTravelersProceed(
    category: AnalyticsCategory,
    adults: number,
    children: number,
    countries: string[]
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: 'number_of_travels',
      value: adults + children,
      adults,
      children,
      countries: countryList,
    })
  }

  sendContactsProceed(
    category: AnalyticsCategory,
    promocode: string,
    isSubscribe: boolean,
    countries: string[]
  ) {
    let promo_code: string | undefined
    let countryList: string[] | undefined
    if (promocode) {
      promo_code = promocode
    }

    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: ANALITYCS_PAGES[InboundFlowRoutes.CONTACTS]?.page,
      subscribed: isSubscribe ? 'Yes' : 'No',
      promo_code,
      country: countryList,
    })
  }

  sendSelectPlanProceed(
    price: number,
    numTravelers: number,
    departureDate: Date,
    returnDate: Date
  ) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.INBOUND,
      page: SELECT_PLAN_PAGE,
      price: String(price),
      num_travelers: numTravelers,
      departure_date: formatAnalyticsDate(departureDate),
      return_date: formatAnalyticsDate(returnDate),
    })
  }

  sendOutboundSelectPlanProceed(
    isSingle: boolean,
    plan: Plan,
    countries: string[],
    numOfTravelers: string,
    discountPrice?: number
  ) {
    this.send(SELECT_PLAN_EVENT, {
      category: TravelDirection.OUTBOUND,
      plan_type: isSingle ? 'single' : 'multiple',
      page: SELECT_PLAN_PAGE,
      plan_name: plan.name,
      schemeCode: plan.schemecode,
      price: String(plan.price),
      countries,
      num_of_travelers: numOfTravelers,
      discount_price: discountPrice,
    })
  }

  sendOutboundNewSPSelect(data: {
    planType: AnalyticsPlanType
    planName: string
    price: number
    countries: string[]
    numOfTravelers: number
    switchesEnabled: NewSPAddonName[]
    numOfAddons: number
  }) {
    this.send(SELECT_PLAN_EVENT, {
      category: TravelDirection.OUTBOUND,
      planType: data.planType,
      page: SELECT_PLAN_PAGE,
      plan_name: data.planName,
      price: data.price,
      countries: data.countries,
      num_of_travelers: data.numOfTravelers,
      switches_enabled: data.switchesEnabled,
      number_of_addons: data.numOfAddons,
    })
  }

  sendSelectPlanChangeDate(price: number, departureDate: Date, returnDate: Date) {
    this.send(CHANGE_DATES_EVENT, {
      category: TravelDirection.INBOUND,
      page: SELECT_PLAN_PAGE,
      price: String(price),
      departure_date: formatAnalyticsDate(departureDate),
      return_date: formatAnalyticsDate(returnDate),
    })
  }

  sendOutboundSelectPlanChangeDate(departureDate: Date, returnDate: Date) {
    this.send(CHANGE_DATES_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      departure: formatAnalyticsDate(departureDate),
      return: formatAnalyticsDate(returnDate),
    })
  }

  sendSelectPlanChangeTravelers(price: number, numTravelers: number) {
    this.send(CHANGE_TRAVELERS_EVENT, {
      category: TravelDirection.INBOUND,
      page: SELECT_PLAN_PAGE,
      price: String(price),
      num_travelers: numTravelers,
    })
  }

  sendOutboundSelectPlanChangeTravelers(adults: number, children: number) {
    this.send(CHANGE_TRAVELERS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      num_travelers: adults + children,
    })
  }

  sendSelectPlanPolicyWordings(price: number) {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      category: TravelDirection.INBOUND,
      page: SELECT_PLAN_PAGE,
      price: String(price),
    })
  }

  sendOutboundSelectPlanPolicyWordings(name: string, price: number) {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      plan_name: name,
      price: String(price),
    })
  }

  sendOutboundPlanDetailsPolicyWordings() {
    this.send(CLICK_POLICY_WORDINGS_EVENT, {
      page: SELECT_PLAN_PAGE,
      popup_name: 'plan_details',
    })
  }

  sendOutboundSelectPlanDestinationChange(countries: string[]) {
    this.send('change_destinations', {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      countries,
    })
  }

  sendOutboundSelectPlanShowPlans(plansGA: ShowPlansEventParams[]) {
    this.send(SHOW_PLANS_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      items: plansGA,
      number_of_plans: plansGA.length,
    })
  }

  sendOutboundSelectPlanPromo(title: string) {
    const page = window.location.pathname.split('/').filter(Boolean).pop() || ''
    this.send('click_link', {
      product: 'travel',
      categoty: 'outbound',
      promo_name: 'ramadan',
      page: page === 'quote' ? 'quote' : 'select_plan',
      link_title: title,
    })
  }

  sendPersonalInformationProceed(
    category: AnalyticsCategory,
    nationality: string,
    countries: string[],
    birthdate: Date,
    gender: string
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: 'holder_info',
      nationality,
      countries: countryList,
      dob: formatAnalyticsDate(birthdate),
      gender,
    })
  }

  sendInsuredTravelersProceed(
    category: AnalyticsCategory,
    numTravelers: number,
    countries: string[]
  ) {
    let countryList: string[] | undefined
    if (category === TravelDirection.OUTBOUND) {
      countryList = countries
    }
    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: 'insured_people',
      num_travelers: numTravelers,
      countries: countryList,
    })
  }

  sendInsuredTravelersEdit(category: AnalyticsCategory) {
    this.send('edit_person', {
      category,
      page: 'insured_people',
    })
  }

  sendInsuredTravelersAdd(category: AnalyticsCategory) {
    this.send('add_person', {
      category,
      page: 'insured_people',
    })
  }

  sendInsuredTravelersAddNew(
    category: AnalyticsCategory,
    relationship: string,
    nationality: string
  ) {
    this.send('added_new_person', {
      category,
      page: 'add_person',
      relationship,
      nationality,
    })
  }

  sendInsuredTravelersDelete(category: AnalyticsCategory) {
    this.send('delete_person', {
      category,
      page: 'insured_people',
    })
  }

  sendInsuredTravelersUpdateProceed(
    category: AnalyticsCategory,
    nationality: string,
    relationName?: string
  ) {
    let relationship: string | undefined

    if (relationName) {
      relationship = relationName
    }

    this.send(CLICK_TO_PROCEED_EVENT, {
      category,
      page: 'update_information',
      nationality,
      relationship,
    })
  }

  sendDestinationProceed(countries: string[]) {
    this.send(CLICK_TO_PROCEED_EVENT, {
      category: TravelDirection.OUTBOUND,
      page: 'destination',
      countries,
    })
  }

  sendQuoteBuy(
    coupon: string,
    transactionId: number,
    numTravelers: number,
    totalPrice: number,
    countries: string[],
    choosenCard: string
  ) {
    this.send('start_purchase', {
      category: TravelDirection.INBOUND,
      page: 'your_quote',
      coupon,
      transaction_id: transactionId,
      num_travelers: numTravelers,
      total_price: String(totalPrice),
      countries,
      choosen_card: choosenCard,
    })
  }

  sendOutboundQuoteBuy(
    coupon: string,
    transactionId: number,
    numTravelers: number,
    addonsPrice: number,
    totalPrice: number,
    listOfAddons: string[],
    countries: string[],
    choosenCard: string,
    number_of_addons: number,
    tabTitle?: 'earn' | 'spend',
    discountPrice?: number
  ) {
    this.send('start_purchase', {
      category: TravelDirection.OUTBOUND,
      page: 'your_quote',
      coupon,
      transaction_id: transactionId,
      num_of_travelers: numTravelers,
      addons_price: String(addonsPrice),
      total_price: String(totalPrice),
      // TODO: Hack. Try to resolve.
      switches_enabled: isProxy(listOfAddons) ? toRaw(listOfAddons) : listOfAddons,
      countries,
      choosen_card: choosenCard,
      number_of_addons,
      tab_title: tabTitle,
      discount_price: discountPrice,
    })
  }

  sendDownloadPolicyEvent(product: string, transactionId: string | number) {
    this.send('view_page', {
      product,
      page: 'download_policy',
      transaction_id: transactionId,
    })
  }

  sendPurchaseEvent(params: PurchaseEventParams) {
    this.send('purchase', {
      ...params,
      coupon: params.coupon || '',
      choosen_card: params.choosen_card || '',
      page: 'your_quote',
    })
  }

  sendToVisitors() {
    this.send('to_visitors', {
      category: TravelDirection.INBOUND,
      page: ANALITYCS_PAGES[InboundFlowRoutes.RESIDENCE]?.page,
    })
  }

  sendContactUsPopup(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('contact_us', {
      category,
      page,
    })
  }

  sendTermsConditions(route: AvailableFlowRoutes, quote_id: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_terms_conditions', {
      category,
      page,
      quote_id,
    })
  }

  sendPaymentTermsConditions(route: AvailableFlowRoutes, quote_id: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_payment_terms_conditions', {
      category,
      page,
      quote_id,
    })
  }

  sendClickPhone(route: AvailableFlowRoutes, phone: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_send_phone', {
      category,
      page,
      phone,
      place: 'popup_contact_us',
    })
  }

  sendWhatsApp(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category

    this.send('click_whatsapp', {
      category,
      page,
      place: 'popup_contact_us',
    })
  }

  sendDownloadDoc(event: string, category: string, transaction_id: string | number) {
    this.send(event, {
      category,
      page: 'download_policy',
      policy_id: transaction_id,
    })
  }

  sendPolicyDownload(policyId: string, category: TravelDirection, btnTitle: string) {
    this.send('click_btn', {
      category,
      page: 'download_policy',
      policy_id: policyId,
      btn_title: btnTitle,
    })
  }

  sendAbTest(group: string, value: string) {
    this.send('exp_get', { group, value })
  }

  sendViewQuote(
    route: AvailableFlowRoutes,
    countries: string[],
    listOfAddons: string[],
    addons: number,
    numOfTravelers: string,
    addonsPrice: number,
    quote_id: string,
    totalPrice: number
  ) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const page = pageData.page
    const category = pageData.category
    this.send('view_quote', {
      category,
      page,
      countries,
      switches_enabled: isProxy(listOfAddons) ? toRaw(listOfAddons) : listOfAddons,
      number_of_addons: addons,
      num_of_travelers: numOfTravelers,
      addons_price: String(addonsPrice),
      quote_id,
      total_price: String(totalPrice),
    })
  }

  sendSwitchAddon(params: SwitchAddonsParams) {
    this.send('switch_addon', {
      ...params,
      category: params.category || TravelDirection.OUTBOUND,
      page: 'your_quote',
    })
  }

  sendQdAppDownload(policyId: string) {
    this.send('download_app', {
      category: TravelDirection.OUTBOUND,
      page: 'download_policy',
      policy_id: policyId,
    })
  }

  sendAppDownloadPromo(policyId: string) {
    this.send('download_app', {
      product: 'travel',
      category: 'outbound',
      page: 'download_policy',
      promo_name: 'ramadan',
      policy_id: policyId,
    })
  }

  sendPersonalInformationFieldCompleted(field: string, position: number) {
    this.send('field_completed', {
      category: TravelDirection.OUTBOUND,
      page: 'holder-info',
      field_name: field,
      field_position: position,
    })
  }

  sendQidFieldCompleted(birthdate: Date, nationality: string) {
    this.send('field_completed', {
      category: TravelDirection.OUTBOUND,
      page: 'holder-info',
      dob: formatAnalyticsDate(birthdate),
      country_of_residense: nationality,
    })
  }

  sendNewSPSwitchAddon(data: {
    planType: AnalyticsPlanType
    planName: string
    state: boolean
    totalPrice: number
    addonName: NewSPAddonName
    addonPrice: number
  }) {
    this.send('switch_addon', {
      category: TravelDirection.OUTBOUND,
      page: SELECT_PLAN_PAGE,
      plan_type: data.planType,
      plan_name: data.planName,
      state: data.state,
      total_price: data.totalPrice,
      addon_name: data.addonName,
      addon_price: data.addonPrice,
    })
  }

  sendCoverToggle(data: {
    category: AnalyticsCategory
    planName: string
    planPrice: number
    coverName: string
    section: SPSection
    numOfTravelers: number
  }) {
    this.send('expand_on', {
      page: SELECT_PLAN_PAGE,
      category: data.category,
      section: data.section,
      num_of_travelers: data.numOfTravelers,
      cover_name: data.coverName,
      plan_name: data.planName,
      price: data.planPrice,
    })
  }

  sendCoverToggleQuote(data: {
    category: AnalyticsCategory
    quoteId: number
    totalPrice: number
    coverName: string
    numOfTravelers: number
    section?: string
  }) {
    this.send(EXPAND_ON_EVENT, {
      page: 'your_quote',
      category: data.category,
      section: data.section ?? 'inclusive_cover_info',
      num_of_travelers: data.numOfTravelers,
      cover_name: data.coverName,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendTravellersToggleQuote(data: {
    category: AnalyticsCategory
    quoteId: number
    totalPrice: number
    numOfTravelers: number
  }) {
    this.send(EXPAND_ON_EVENT, {
      page: 'your_quote',
      category: data.category,
      section: 'expand_list_of_travellers',
      num_of_travelers: data.numOfTravelers,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendEditTravellersQuote(data: {
    category: AnalyticsCategory
    countries: string[]
    quoteId: number
    totalPrice: number
    numOfTravelers: number
  }) {
    this.send('edit_person', {
      page: 'your_quote',
      category: data.category,
      countries: data.countries,
      num_of_travelers: data.numOfTravelers,
      quote_id: data.quoteId,
      total_price: data.totalPrice,
    })
  }

  sendCyberTooltip(category: AnalyticsCategory, page: string, text: string, quote_id?: number) {
    this.send('click_icon', {
      category,
      page,
      text,
      quote_id,
    })
  }

  sendCyberPopupOpened(route: AvailableFlowRoutes, quote_id?: number) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      label: 'cyber_security',
      ...(pageData.page === 'your_quote' && { quote_id }),
    }

    this.send(VIEW_POPUP_EVENT, payload)
  }

  sendPlanDetailsPopupOpened(route: AvailableFlowRoutes) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      popup_name: 'plan_details',
    }

    this.send(VIEW_POPUP_EVENT, payload)
  }

  sendPlanTabClick(route: AvailableFlowRoutes, tab: string) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      popup_name: 'plan_details',
      click_btn: tab,
    }

    this.send('click_btn', payload)
  }

  sendAddCyberToCoverage(route: AvailableFlowRoutes, quote_id: number) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const payload: Record<string, string | number> = {
      page: pageData.page,
      label: 'cyber_security',
      quote_id,
      click_btn: 'add_to_main_coverage',
    }

    this.send('click_btn', payload)
  }

  sendClickLearnMore(
    route: AvailableFlowRoutes,
    options?: {
      quote_id?: number
      state?: boolean
      section?: string
      cover_name?: string
    }
  ) {
    const pageData = ANALITYCS_PAGES[route]
    if (!pageData) return

    const { quote_id, state, section, cover_name } = options || {}

    const payload: Record<string, string | number | boolean> = {
      page: pageData.page,
      category: pageData.category,
      ...(cover_name !== undefined && { cover_name }),
      ...(pageData.page === 'your_quote' && { quote_id }),
      ...(pageData.page === 'your_quote' && { state }),
      ...(section !== undefined && { section }),
    }

    this.send(LEARN_MORE_EVENT, payload)
  }

  sendGoToStore(policyId: string, category: TravelDirection) {
    this.send('click_btn', {
      plan_name: category,
      page: 'success_page',
      policy_id: policyId,
      btn_title: 'go_to_store',
    })
  }

  sendSeeEarnedCoins(policyId: string, category: TravelDirection, bonusesAmount: number) {
    this.send('click_btn', {
      plan_name: category,
      page: 'success_page',
      policy_id: policyId,
      btn_title: 'see_more',
      bonus_value: bonusesAmount,
    })
  }

  sendWebViewApp(googleId: string) {
    this.send('come_from_app', {
      google_id: googleId,
    })
  }

  sendAiraloPopupOpened() {
    this.send('click_banner', {
      page: 'your_quote',
      banner_title: 'airalo_discount',
    })
  }

  sendAiraloPopupClosed(btn_title?: string) {
    this.send('close_popup', {
      page: 'your_quote',
      popup_name: 'airalo_discount',
      btn_title,
    })
  }

  sendAiraloDiscountBanner() {
    this.send('click_btn', {
      page: 'download_policy',
      btn_title: 'use_discount',
      section: 'banner',
    })
  }

  sendCoinsRewardTabClick(
    option: 'earn' | 'spend',
    category: TravelDirection,
    numTravelers: number,
    quoteId: number,
    totalPrice: number,
    discountPrice?: number
  ) {
    this.send('click_tab', {
      page: 'your_quote',
      category,
      tab_title: option,
      num_of_travelers: numTravelers,
      quote_id: quoteId,
      total_price: totalPrice,
      discount_price: discountPrice,
    })
  }

  sendClickOoredooBanner(policyId: string) {
    this.send('click_btn', {
      policy_id: policyId,
      page: 'download_policy',
      btn_title: 'see_plans',
    })
  }

  sendClickSelectPlanLearnMore() {
    this.send('click_learn_more', {
      page: 'select_plan',
      section: 'plans_block',
      category: 'outbound',
    })
  }

  sendClickSelectPlanWhatsCovered() {
    this.send('what_is_covered', {
      page: 'select_plan',
      category: 'outbound',
    })
  }
  sendClickSelectPlanBackToPlans() {
    this.send('click_btn', {
      page: 'select_plan',
      category: 'outbound',
      btn_title: 'back_to_plans',
    })
  }
}
